/**
 * Haptics — one call site for every buzz the UI asks for.
 *
 * On the web this does nothing: there is no sink. It is still called at every
 * moment a native app would buzz — a swipe crossing its threshold, a commit, a
 * warning — so that when the native shell lands (ROADMAP, native track) it
 * installs a sink once, here, and every gesture already speaks. Components
 * reach it through `useHaptics()`.
 *
 * Deliberately not `navigator.vibrate`: iOS Safari has no Vibration API, and
 * buzzing Android browsers only would make the same gesture feel different on
 * two phones for no reason the user could see.
 */

import { getHapticsEnabled } from "./haptics_preference";
/**
 * `key` is a numpad tap (light); `commit` a Save (medium); `threshold` a swipe
 * crossing the point where letting go acts; `warning` a write that carries a
 * category past 80% or 100% of its budget (`crossedMarks`); `select` a changed
 * selection; `tap` a visible reply arriving; `success` a completed save or sync;
 * `error` a failed action or visible error reply.
 */
export type HapticKind = "key" | "commit" | "threshold" | "warning" | "select" | "tap" | "success" | "error";

type HapticsSink = (kind: HapticKind) => void | Promise<void>;

let sink: HapticsSink | null = null;

/** Install the platform's haptics, or null to remove it. */
export function setHapticsSink(next: HapticsSink | null): void {
  sink = next;
}

/** Ask for a haptic. A no-op where the platform provides none. */
export function haptics(kind: HapticKind): void {
  if (!getHapticsEnabled()) return;
  const result = sink?.(kind);
  if (result) void result.catch((cause: unknown) => { console.warn("Haptic feedback failed:", cause); });
}
